/** The assistants the server can answer a Copilot question with. */
export type AssistModel = 'gemini' | 'gpt'

/** Both of them, in the order the panel offers them. */
export const ASSIST_MODELS: readonly AssistModel[] = ['gemini', 'gpt']

/**
 * What the server answers with when nothing has been chosen.
 *
 * The panel shows this for `selected: null` rather than showing nothing, so a
 * reader who never pressed anything still sees the model their questions are
 * actually going to. Sending it explicitly and sending nothing at all reach
 * the same place; the panel sends it so the two sides always agree.
 */
export const DEFAULT_ASSIST_MODEL: AssistModel = 'gemini'

/**
 * @param value Anything off a socket frame or out of persisted settings.
 * @returns The model, or null for a value this build does not know — an older
 *   stored choice, or a model a newer server has that this panel does not.
 */
export function parseAssistModel(value: unknown): AssistModel | null {
  return value === 'gemini' || value === 'gpt' ? value : null
}

export interface AssistModelStatus {
  /** Null when nothing was chosen and the server is on its own default. */
  selected: AssistModel | null
  /** The models this server has configured. Empty when it named none. */
  available: AssistModel[]
}

/**
 * Read an `assist.model.status` frame.
 *
 * Unknown names are dropped from `available` rather than failing the frame: a
 * server that gains a third model should not take the two buttons away from a
 * panel that predates it.
 *
 * @param event The frame as it arrived.
 * @returns The status, or null when the frame is not an object at all.
 */
export function parseAssistModelStatus(event: unknown): AssistModelStatus | null {
  if (typeof event !== 'object' || event === null) return null
  const record = event as Record<string, unknown>
  const available = Array.isArray(record.available)
    ? record.available
        .map(parseAssistModel)
        .filter((model): model is AssistModel => model !== null)
    : []

  return { selected: parseAssistModel(record.selected), available }
}
